/**
 * Registration step 1: the "Open a new account" form on `/register` (rendered twice, desktop and
 * mobile, by `Register.tsx`). It collects e-mail, first and last name, company and company postal
 * code, obtains an invisible reCAPTCHA token, and posts them to the Users API
 * `public/registration/send-token` (`APIUrl.REGISTER_SEND_TOKEN`;
 * `web-users-api/application/src/functions/PostPublicRegistrationSendToken/postPublicRegistrationSendToken.ts`).
 * That route stores the details with a one-time code, e-mails the code, and publishes a sign-up
 * event to Salesforce Marketing Cloud. On HTTP 200 this form sends the GA `SIGN_UP` event and
 * navigates to `/register/verify` with `{ user }`: the whole request body, reCAPTCHA token and
 * tracking fields included. The step order is in `pages/register/index.tsx`.
 *
 * Rules a change must keep:
 * - The body is the Users API's `IRegistrationSendTokenRequest`
 *   (`web-users-api/application/src/models/user.model.ts`). Its validator
 *   (`web-users-api/application/src/validators/registrationWithCompanyName.validator.ts`) requires
 *   `firstName`, `lastName`, `email`, `zipCode` and `companyName`, and account creation reads
 *   them back from the stored code, so these are the account's details.
 * - `user.email` is read by step 2 (`VerifyToken/RegisterVerifyTokenRoot.tsx`), and `user.country`
 *   and `user.language` by step 3 (`CreateAccount/CreateAccountRoot.tsx`). Renaming a
 *   {@link RegistrationTokenRequest} field breaks those steps.
 * - The request carries `recaptchaToken`. web-architecture ADR-0032 makes the Users API's check of
 *   it mandatory; today the API checks it only when present.
 *
 * Failure behaviour (ADR-0032 records the missing `catch` and leaves it as it is): the send has no
 * `catch`, and under the shared interceptor every error status except 401 rejects (ADR-0041), so a
 * refused request shows the visitor no message. The Users API answers HTTP 200 with a body of
 * `500` when no code was created; only the HTTP status is checked here, so that case still moves
 * on to `/register/verify`. `register` is never set back to `false`, so after a failed send a
 * further click does not send again until the page is reloaded.
 *
 * Related: `RegisterContent.ts` (heading copy), `components/constants.ts` → `maxFirstNameLength`
 * and `maxLastNameLength`, `enums/apiUrl.enums.ts` → `APIUrl`.
 */
import React, { createRef, useEffect, useState } from "react";
import { css } from "@emotion/react";
import { useRouter } from "next/router";
import { Flex, FlexItem } from "@ryerson/frontend.layout";
import { Button } from "@ryerson/frontend.button";
import { Typography } from "@ryerson/frontend.typography";
import { Link } from "@ryerson/frontend.navigation";
import { EmailInput, Input, ZipCodeInput } from "@ryerson/frontend.form-controls";
import { Language, useApplication } from "@ryerson/frontend.application";
import { navigate } from "@ryerson/frontend.next";
import { APIUrl } from "@enums/apiUrl.enums";
import {
	sendGAEvent,
	PostalCodeRegexPatterns,
	Country,
	GoogleAnalyticsEventName,
	validateEmail,
} from "@ryerson/frontend.common";
import ReCAPTCHA from "react-google-recaptcha";
import { RecaptchaDisclaimer, validateZipCode } from "@ryerson/frontend.common";
import styled from "@emotion/styled";
import { RegisterFormContent } from "./RegisterContent";
import { maxFirstNameLength, maxLastNameLength } from "@components/constants";

/**
 * The invisible reCAPTCHA widget with Google's floating badge hidden. The form shows
 * `RecaptchaDisclaimer` text below the button instead.
 */
const InvisibleRecaptcha = styled(ReCAPTCHA as any)`
	.grecaptcha-badge {
		visibility: hidden;
	}
`;
/** `isMobile` selects the mobile padding and heading size; `Register.tsx` renders one of each. */
export interface RegisterFormProps {
	className?: string;
	isMobile: boolean;
}

/**
 * The send-token request body, and the `user` state that steps 2 and 3 receive through
 * `navigate` (`VerifyToken/RegisterVerifyTokenRoot.tsx`, `CreateAccount/CreateAccountRoot.tsx`,
 * and the `user` check in `components/Login/hooks/useRedirectToRegisterIfNoUser.ts`).
 */
export interface RegistrationTokenRequest {
	firstName: string;
	lastName: string;
	/** The site language at the time of sign-up; step 3 sends it as the account's language. */
	language?: Language;
	/**
	 * Derived from the postal code's format (`getRegexPatternCountry`), `us` when nothing matches.
	 */
	country?: Country;
	email: string;
	companyName?: string;
	zipCode: string;
	/** Token from the invisible reCAPTCHA, verified by the Users API (ADR-0032). */
	recaptchaToken: string;
}

/**
 * The body actually sent: {@link RegistrationTokenRequest} plus the visit's acquisition source.
 * The Users API passes `source` and `medium` into the Marketing Cloud sign-up event
 * (`web-users-api/application/src/mappers/salesForce.mapper.ts` →
 * `mapToSignUpSalesForceRequestModel`).
 */
export interface RegistrationTokenTrackingRequest extends RegistrationTokenRequest {
	source: string;
	medium: string;
}

/** Acquisition source and medium; empty strings when the URL carries no tracking parameter. */
interface TrackingParams {
	source: string;
	medium: string;
}

/** The registration step 1 form; see the file header. */
const RegisterForm: React.FC<RegisterFormProps> = ({ className = "", isMobile }) => {
	const router = useRouter();
	const {
		axiosInstance,
		localization: { language },
	} = useApplication();
	const [register, setRegister] = React.useState<boolean>(false);
	const [submitted, setSubmitted] = useState<boolean>(false);
	const [email, setEmail] = React.useState<string>("");
	const [zipCode, setZipCode] = React.useState<string>("");
	const [firstName, setFirstName] = React.useState<string>("");
	const [lastName, setLastName] = React.useState<string>("");
	const [companyName, setCompanyName] = React.useState<string>("");
	const [recaptchaToken, setRecaptchaToken] = useState<string>();
	const [doRecaptchaTest, setDoRecaptchaTest] = useState<boolean>(false);

	const recaptchaSiteKey = process.env.NEXT_PUBLIC_RECAPTCHA_SITE_KEY;
	const recaptchaRef = createRef<ReCAPTCHA>();

	const content = RegisterFormContent[language || Language.en];

	// Ad-click and campaign parameters on the landing URL: Google Ads (`gclid`), Microsoft Ads
	// (`msclkid`) and UTM tags. Read once per render; see getTrackingParams.
	const { gclid, msclkid, utm_source, utm_medium } = router.query;

	const emailValidation = () => {
		return validateEmail(email, true) === "";
	};

	const zipCodeValidation = () => {
		return validateZipCode(zipCode, "", language) === "";
	};

	// Both validators return true when the form is NOT ready. `(a && b && c && d) === ""` is true
	// when any of the four is empty, because `&&` yields the first empty string. E-mail and
	// postal-code format are checked only after the first submit.
	// This one, with the company name, disables the "Get Started" button.
	const validateRegisterFormWithCompanyName = () => {
		return (
			(firstName && lastName && email && zipCode) === "" ||
			(submitted && !emailValidation()) ||
			(submitted && !zipCodeValidation()) ||
			companyName === ""
		);
	};

	// Used only by the Enter key (handleKeypress). It does not check the company name, so Enter can
	// submit with the company empty; the Users API validator then refuses the request.
	const validateRegisterForm = () => {
		return (
			(firstName && lastName && email && zipCode) === "" ||
			(submitted && !emailValidation()) ||
			(submitted && !zipCodeValidation())
		);
	};

	/**
	 * The country whose postal-code pattern in `PostalCodeRegexPatterns`
	 * (`@ryerson/frontend.common`) first matches the entered code, in the object's key order; `us`
	 * when none matches. Sent as `country`, which step 3 passes on to account creation.
	 */
	const getRegexPatternCountry = (): Country => {
		const regexPattern = Object.entries(PostalCodeRegexPatterns).find(([country, regx]) => {
			let regex = new RegExp(regx, "g");
			if (regex.test(zipCode)) {
				return true;
			}
			return false;
		});

		return (regexPattern ? regexPattern[0] : "us") as Country;
	};

	/**
	 * Acquisition source for the sign-up event. First match wins: `gclid` gives `google`/`cpc`,
	 * `msclkid` gives `bing`/`cpc`, otherwise `utm_source`/`utm_medium` as given.
	 */
	const getTrackingParams = (): TrackingParams => {
		const params: TrackingParams = {
			source: "",
			medium: "",
		};

		if (!!gclid) {
			params.source = "google";
			params.medium = "cpc";
		} else if (!!msclkid) {
			params.source = "bing";
			params.medium = "cpc";
		} else if (!!utm_source) {
			params.source = utm_source as string;
			params.medium = (utm_medium as string) || "";
		}

		return params;
	};

	// The send. It runs when `register` turns true (set by handleFormSubmit or onSubmitRecaptcha
	// once the token exists and the e-mail and postal code are valid). `register` is never reset,
	// so this sends at most once per page load. No `catch`: see the file header.
	useEffect(() => {
		if (register && !!recaptchaToken) {
			let user: RegistrationTokenTrackingRequest = {
				firstName: firstName,
				lastName: lastName,
				email: email,
				companyName: companyName,
				zipCode: zipCode,
				recaptchaToken: recaptchaToken,
				language,
				country: getRegexPatternCountry() as Country,
				...getTrackingParams(),
			};

			axiosInstance.post(`${APIUrl.REGISTER_SEND_TOKEN}`, user).then((response) => {
				if (response.status && response.status === 200) {
					sendGAEvent(GoogleAnalyticsEventName.SIGN_UP);
					navigate("/register/verify", { state: { user } });
				}
			});
		}
	}, [register]);

	const handleKeypress = (e: any) => {
		if (e.keyCode === 13) {
			!validateRegisterForm() && handleFormSubmit();
		}
	};

	/**
	 * Runs the invisible challenge once the reCAPTCHA script has loaded, and keeps the token it
	 * returns.
	 */
	const onLoadRecaptcha = async () => {
		const token = await recaptchaRef.current?.executeAsync();
		if (!!token) {
			setRecaptchaToken(token);
		}
	};

	/**
	 * "Get Started" and Enter. With a token already held, validates and sends. Without one, the
	 * first click mounts the reCAPTCHA widget (`doRecaptchaTest`), whose script load runs the
	 * challenge and whose `onChange` (onSubmitRecaptcha) then validates and sends; a later click
	 * while the widget is mounted runs the challenge again.
	 */
	const handleFormSubmit = () => {
		if (!!recaptchaToken) {
			setSubmitted(true);
			if (emailValidation() && zipCodeValidation()) {
				setRegister(true);
			}
		} else {
			if (doRecaptchaTest) {
				onLoadRecaptcha();
			} else {
				setDoRecaptchaTest(true);
			}
		}
	};

	/**
	 * Widget callback with a solved token: keeps it, unmounts the widget, then validates and sends.
	 */
	const onSubmitRecaptcha = (token: string | null) => {
		if (!!token) {
			setRecaptchaToken(token);
			setDoRecaptchaTest(false);
			setSubmitted(true);
			if (emailValidation() && zipCodeValidation()) {
				setRegister(true);
			}
		}
	};
	return (
		<FlexItem
			className={className}
			css={{
				width: "100%",
				padding: isMobile ? "0px 0px 40px 0px" : "25px 35px 65px 20px",
			}}
		>
			<Flex direction="column">
				<Typography
					variant={isMobile ? "h3" : "h4"}
					css={{
						marginTop: isMobile ? "16px" : "23px",
						marginBottom: isMobile ? "16px" : "23px",
					}}
				>
					{content.openNew}
				</Typography>
			</Flex>
			<Flex alignContent="space-between">
				<Typography size="md">{content.alreadyHave}</Typography>
				&nbsp;
				<Link type="secondary" to="/login" size="md">
					{content.logIn}
				</Link>
			</Flex>
			<br />
			<br />
			<EmailInput
				required
				value={email}
				placeholder=""
				onChange={(event) => setEmail(event.target.value)}
				onKeyDown={handleKeypress}
				submitted={submitted}
				isValid={submitted ? emailValidation() : true}
				language={language}
			/>
			<br />
			<Input
				label="First Name"
				name="firstName"
				placeholder={""}
				required={true}
				submitted={submitted}
				value={firstName}
				onChange={(event) => setFirstName(event.target.value)}
				onKeyDown={handleKeypress}
				language={language}
				maxLength={maxFirstNameLength}
			/>
			<br />
			<Input
				label="Last Name"
				name="lastName"
				placeholder={""}
				required={true}
				submitted={submitted}
				value={lastName}
				onChange={(event) => setLastName(event.target.value)}
				onKeyDown={handleKeypress}
				language={language}
				maxLength={maxLastNameLength}
			/>
			<br />
			<Input
				maxLength={40}
				label="Company"
				name="companyName"
				placeholder={""}
				required={true}
				submitted={submitted}
				value={companyName}
				onChange={(event) => setCompanyName(event.target.value)}
				onKeyDown={handleKeypress}
				language={language}
			/>
			<br />
			<ZipCodeInput
				label="Company Postal Code"
				name="zipCode"
				placeholder={""}
				required
				submitted={submitted}
				value={zipCode}
				onChange={(event) => setZipCode(event.target.value)}
				onKeyDown={handleKeypress}
				language={language}
				isValid={!submitted}
			/>
			<br />
			<br />
			<Flex
				direction="column"
				style={css`
					width: ${isMobile ? "100%" : "160px"};
				`}
			>
				{/* Mounted only while a challenge is pending. `hl` (challenge language) is French
				    for `fr`, English for `en` and also for `es`. */}
				{doRecaptchaTest && (
					<InvisibleRecaptcha
						ref={recaptchaRef}
						hl={
							language === Language.fr
								? Language.fr
								: language === Language.es
									? Language.en
									: "en"
						}
						sitekey={recaptchaSiteKey || ""}
						asyncScriptOnLoad={onLoadRecaptcha}
						onChange={onSubmitRecaptcha}
						size="invisible"
						onExpired={() => {
							recaptchaRef.current?.reset();
						}}
					/>
				)}
				<Button
					label="Get Started"
					onClick={handleFormSubmit}
					disabled={validateRegisterFormWithCompanyName()}
					fullwidth={true}
				/>
			</Flex>
			<Flex
				style={css`
					width: 55%;
					padding-top: 20px;
				`}
			>
				<RecaptchaDisclaimer language={language} />
			</Flex>
		</FlexItem>
	);
};

export default RegisterForm;
